import React from 'react';
import {
    Switch,
    Route,
    Redirect,
    Router,
    RouteComponentProps,
} from 'react-router-dom';
import core, { App as AppTypes } from '@gin-betting/core';

import { ExtendedRouts } from 'app-constants';
import { ScrollToTop } from 'components';
import App from '../root/App';
import Upcoming from 'views/Upcoming';
import Live from 'views/Live';
import FavoriteList from 'views/FavoriteList';
import Outrights from 'views/Outrights';
import Outright from 'views/Outright';
import Results from 'views/Results';
import Prematch from 'views/Prematch';
import { AsyncMatch } from 'modules/match/async';
import tournamentModule from 'modules/tournament';
import EntityIdResolver from './EntityIdResolver';
import EntityIdRouteResolver from './EntityIdRouteResolver';
import Search from 'views/Search';

const {
    services: { getHistory },
} = core;
const {
    components: { TournamentComponent },
} = tournamentModule;

function applyBrowserLocationBlocker(history: ReturnType<typeof getHistory>) {
    let currentLocation: string | null = null;

    history.block((location, action) => {
        const nextLocation =
            location.pathname + location.search + location.hash;

        if (action === 'PUSH') {
            if (currentLocation === nextLocation) {
                return false;
            }
        }

        currentLocation = nextLocation;
    });
}

const InnerRouts: React.FC<RouteComponentProps> = ({
    match: { path },
}): JSX.Element => {
    return (
        <Switch>
            <Route
                exact={true}
                path={`${path}${AppTypes.Routes.UPCOMING_LIST}`}
                component={Upcoming}
            />
            <Route
                path={`${path}${AppTypes.Routes.OUTRIGHTS_LIST}`}
                component={Outrights}
            />
            <Route
                path={`${path}${AppTypes.Routes.RESULTS_LIST}`}
                component={Results}
            />
            <Route
                exact={true}
                path={`${path}${AppTypes.Routes.LIVE_LIST}`}
                component={Live}
            />
            <Route
                path={`${path}${AppTypes.Routes.PREMATCH}`}
                component={Prematch}
            />
            <Route
                path={`${path}${AppTypes.Routes.OUTRIGHT}:slug`}
                render={props => (
                    <EntityIdResolver
                        sportEventId={props.match.params.slug}
                        slug={props.match.params.slug}
                    >
                        {sportEventId => (
                            <Outright matchId={sportEventId} {...props} />
                        )}
                    </EntityIdResolver>
                )}
            />
            <Route
                path={`${path}${AppTypes.Routes.RESULT_MATCH}:slug`}
                render={props => (
                    <AsyncMatch slugOrId={props.match.params.slug} />
                )}
            />
            <Route
                path={`${path}${AppTypes.Routes.MATCH}:slug`}
                render={props => (
                    <AsyncMatch slugOrId={props.match.params.slug} />
                )}
            />
            <Route
                path={`${path}${AppTypes.Routes.TOURNAMENT}:slug`}
                render={props => (
                    <EntityIdResolver
                        slug={props.match.params.slug}
                        tournamentId={props.match.params.slug}
                    >
                        {tournamentId => (
                            <TournamentComponent tournamentId={tournamentId} />
                        )}
                    </EntityIdResolver>
                )}
            />
            <Redirect to={path} />
        </Switch>
    );
};

export default function createRoutes() {
    const history = getHistory();
    applyBrowserLocationBlocker(history);

    return (
        <Router history={history}>
            <App>
                <ScrollToTop>
                    <Switch>
                        <Route
                            exact={true}
                            path={AppTypes.Routes.UPCOMING_LIST}
                            component={Upcoming}
                        />
                        <Route
                            path={AppTypes.Routes.OUTRIGHTS_LIST}
                            component={Outrights}
                        />
                        <Route
                            path={AppTypes.Routes.RESULTS_LIST}
                            component={Results}
                        />
                        <Route
                            exact={true}
                            path={AppTypes.Routes.LIVE_LIST}
                            component={Live}
                        />
                        <Route
                            path={AppTypes.Routes.PREMATCH}
                            component={Prematch}
                        />
                        <Route
                            path={`${AppTypes.Routes.MATCH}:slug`}
                            render={props => (
                                <EntityIdRouteResolver
                                    slug={props.match.params.slug}
                                    path={AppTypes.Routes.MATCH}
                                />
                            )}
                        />
                        <Route
                            path={`${AppTypes.Routes.TOURNAMENT}:slug`}
                            render={props => (
                                <EntityIdRouteResolver
                                    slug={props.match.params.slug}
                                    path={AppTypes.Routes.TOURNAMENT}
                                />
                            )}
                        />
                        <Route
                            path={ExtendedRouts.FAVORITE_UPCOMING}
                            component={FavoriteList}
                        />
                        <Route
                            path={ExtendedRouts.FAVORITE_OUTRIGHTS}
                            component={FavoriteList}
                        />
                        <Route
                            path={ExtendedRouts.FAVORITE_RESULTS}
                            component={FavoriteList}
                        />
                        <Route
                            path={AppTypes.Routes.CLASSIC}
                            component={InnerRouts}
                        />
                        <Route
                            path={AppTypes.Routes.ESPORT}
                            component={InnerRouts}
                        />
                        <Route path={ExtendedRouts.SEARCH} component={Search} />
                        <Redirect to={AppTypes.Routes.UPCOMING_LIST} />
                    </Switch>
                </ScrollToTop>
            </App>
        </Router>
    );
}
